import type React from 'react';

import { Leaf } from '@/core/tamagoshi/icons/Leaf';
import { Menu } from '@/core/tamagoshi/icons/Menu';
import { AttributesTable } from '@/domains/productDiscovery/ProductPage/common/components/Attributes/components/AttributesTable/AttributesTable';
import {
  attributesTitle,
  sustainabilityAttributesTitle,
} from '@/domains/productDiscovery/ProductPage/common/components/Attributes/translations';
import { settings } from '@/domains/productDiscovery/ProductPage/common/components/Attributes/utils/settings.default';

type TableType = 'regular' | 'sustainability';
export type TableColumnsWeight = Required<
  React.ComponentProps<typeof AttributesTable>
>['columnsWeight'];

type AttributesProps = Pick<
  React.ComponentProps<typeof AttributesTable>,
  'defaultExpanded' | 'attributes' | 'labels' | 'gtmProps'
> & {
  id?: Partial<Record<TableType, string>>;
  behavior?:
    | 'unfold'
    | 'unfold-fold'
    | {
        type: 'action';
        action: Record<TableType, () => unknown>;
      };
};

const { maxRowsDisplayed, columnsWeight } = settings;

export const Attributes: React.FC<AttributesProps> = ({
  attributes,
  id,
  behavior = 'unfold',
  ...props
}) => {
  const regularAttributes = attributes.filter(
    (attr) => !attr.isSustainablyRelevant,
  );
  const sustainabilityAttributes = attributes.filter(
    (attr) => attr.isSustainablyRelevant,
  );

  return (
    <>
      <AttributesTable
        {...props}
        id={id?.regular}
        attributes={regularAttributes}
        theme="color"
        title={
          <>
            {attributesTitle}
            <Menu width={12} height={12} />
          </>
        }
        behavior={
          typeof behavior === 'string'
            ? behavior
            : { type: 'action', action: behavior.action.regular }
        }
        maxRowsDisplayed={maxRowsDisplayed}
        columnsWeight={columnsWeight}
      />
      {sustainabilityAttributes.length > 0 && (
        <AttributesTable
          {...props}
          id={id?.sustainability}
          attributes={sustainabilityAttributes}
          theme="gray"
          title={
            <>
              {sustainabilityAttributesTitle}
              <Leaf width={12} height={12} />
            </>
          }
          behavior={
            typeof behavior === 'string'
              ? behavior
              : { type: 'action', action: behavior.action.sustainability }
          }
          maxRowsDisplayed={maxRowsDisplayed}
          columnsWeight={columnsWeight}
        />
      )}
    </>
  );
};
